import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";

const FreelancerSchedule = () => {
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [isBoothRenter, setIsBoothRenter] = useState(false);
  const [salonHours, setSalonHours] = useState(null);
  const [blockForm, setBlockForm] = useState({
    startDate: "",
    endDate: "",
    isFullDayBlock: true,
    timeBlocks: [{ startTime: "09:00", endTime: "17:00" }],
    type: "blocked",
    reason: "",
  });
  const [editingException, setEditingException] = useState(null);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  // Normalize ShopLocation.schedule ({ active, open, close }) to UI shape ({ isOpen, openTime, closeTime })
  const normalizeSalonSchedule = (rawSchedule) => {
    if (!rawSchedule || typeof rawSchedule !== "object") return null;

    const dayOrder = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];
    const normalized = {};

    dayOrder.forEach((day) => {
      const rawDay = rawSchedule[day] || rawSchedule[day.toLowerCase()] || {};
      normalized[day] = {
        isOpen: rawDay.isOpen ?? rawDay.active ?? false,
        openTime: rawDay.openTime || rawDay.open || "09:00",
        closeTime: rawDay.closeTime || rawDay.close || "17:00",
      };
    });

    return normalized;
  };

  // Check if user is R7 (booth renter)
  useEffect(() => {
    const detectR7AndLoadHours = async () => {
      let isR7 = false;

      const rolesStr = localStorage.getItem("ogestyle_roles");
      if (rolesStr) {
        try {
          const roles = JSON.parse(rolesStr);
          isR7 = Array.isArray(roles) && roles.includes("R7");
        } catch (e) {
        }
      }

      // Fallback: detect role from profile if localStorage roles are stale/missing.
      if (!isR7) {
        try {
          const token = localStorage.getItem("ogestyle_token");
          if (token) {
            const res = await fetch(`${API_BASE_URL}/api/freelancer/profile`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const data = await res.json();
              isR7 = data?.staff?.role === "R7";
            }
          }
        } catch (profileErr) {
        }
      }

      setIsBoothRenter(isR7);
      if (isR7) {
        fetchSalonHours();
      }
    };

    detectR7AndLoadHours();
  }, []);

  // Auth check on mount
  useEffect(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  // Fetch salon hours for R7 booth renters
  const fetchSalonHours = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${API_BASE_URL}/api/booth-rental/my-dashboard`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        const data = await response.json();
        const rental =
          data.boothRentals?.find(
            (r) => r.status === "active" || r.status === "pending",
          ) || data.boothRentals?.[0];

        if (rental?.location?.schedule) {
          const normalized = normalizeSalonSchedule(rental.location.schedule);
          setSalonHours(normalized);
        }
      }
    } catch (error) {
    }
  };

  // Fetch schedule
  const fetchSchedule = useCallback(async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/freelancer/schedule`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        // Backend returns { weeklyHours, bufferMinutes } directly
        if (data.weeklyHours) {
          setSchedule({
            weeklyHours: data.weeklyHours,
            bufferMinutes: data.bufferMinutes || 0,
          });
        } else if (data.schedule && data.schedule.weeklyHours) {
          // Fallback for old format
          setSchedule(data.schedule);
        } else if (data.schedule) {
          // Convert old format to new format
          const weeklyHours = {};
          Object.keys(data.schedule).forEach((day) => {
            const dayData = data.schedule[day];
            weeklyHours[day] = {
              isAvailable: dayData.isOpen || dayData.isAvailable || false,
              start: dayData.openTime || dayData.start || "09:00",
              end: dayData.closeTime || dayData.end || "17:00",
            };
          });
          setSchedule({ weeklyHours });
        } else {
          // Set default schedule
          setSchedule({
            weeklyHours: {
              monday: { isAvailable: true, start: "09:00", end: "17:00" },
              tuesday: { isAvailable: true, start: "09:00", end: "17:00" },
              wednesday: { isAvailable: true, start: "09:00", end: "17:00" },
              thursday: { isAvailable: true, start: "09:00", end: "17:00" },
              friday: { isAvailable: true, start: "09:00", end: "17:00" },
              saturday: { isAvailable: false, start: "09:00", end: "17:00" },
              sunday: { isAvailable: false, start: "09:00", end: "17:00" },
            },
          });
        }
      } else if (response.status === 401) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      }
    } catch (error) {
    }
  }, [API_BASE_URL, navigate]);

  // Fetch exceptions
  const fetchExceptions = useCallback(async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${API_BASE_URL}/api/schedule-exceptions/staff`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setExceptions(data.exceptions || []);
      } else if (response.status === 401) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      }
    } catch (error) {
    }
  }, [API_BASE_URL, navigate]);

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(`${API_BASE_URL}/api/bookings/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
      } else if (response.status === 401) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      }
    } catch (error) {
    }
  }, [API_BASE_URL, navigate]);

  useEffect(() => {
    fetchSchedule();
    fetchExceptions();
    fetchBookings();
  }, [fetchSchedule, fetchExceptions, fetchBookings]);

  // Handle schedule update
  const handleScheduleUpdate = async (updatedWeeklyHours) => {
    try {
      const token = localStorage.getItem("ogestyle_token");

      // Ensure weeklyHours is an object and has all days
      const weeklyHours = updatedWeeklyHours || {};
      const daysOfWeek = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      daysOfWeek.forEach((day) => {
        if (!weeklyHours[day]) {
          weeklyHours[day] = {
            isAvailable: false,
            start: "09:00",
            end: "17:00",
          };
        }
      });
      const response = await fetch(`${API_BASE_URL}/api/freelancer/schedule`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ weeklyHours }),
      });
      if (response.ok) {
        const data = await response.json();
        alert("Schedule updated successfully!");
        fetchSchedule();
      } else {
        const error = await response.json();
        alert(error.message || "Failed to update schedule");
      }
    } catch (error) {
      alert("Failed to update schedule");
    }
  };

  // Handle block creation/update
  const handleBlockSubmit = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");

      if (!blockForm.startDate || !blockForm.endDate) {
        alert("Start date and end date are required");
        return;
      }

      const startDateObj = new Date(`${blockForm.startDate}T12:00:00`);
      const endDateObj = new Date(`${blockForm.endDate}T12:00:00`);

      if (
        Number.isNaN(startDateObj.valueOf()) ||
        Number.isNaN(endDateObj.valueOf())
      ) {
        alert("Invalid date format. Please choose valid dates.");
        return;
      }

      if (endDateObj < startDateObj) {
        alert("End date cannot be before start date.");
        return;
      }

      const payload = {
        startDate: blockForm.startDate, // Canonical YYYY-MM-DD
        endDate: blockForm.endDate, // Canonical YYYY-MM-DD
        isFullDayBlock: blockForm.isFullDayBlock,
        timeBlocks: blockForm.isFullDayBlock ? [] : blockForm.timeBlocks,
        type: blockForm.type,
        reason: blockForm.reason,
      };
      const url = editingException
        ? `${API_BASE_URL}/api/schedule-exceptions/${editingException._id}`
        : `${API_BASE_URL}/api/schedule-exceptions`;

      const method = editingException ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        alert(
          editingException
            ? "Block updated successfully!"
            : "Block created successfully!",
        );
        setShowBlockModal(false);
        setEditingException(null);
        fetchExceptions();
        fetchBookings();
        // Reset form
        setBlockForm({
          startDate: "",
          endDate: "",
          isFullDayBlock: true,
          timeBlocks: [{ startTime: "09:00", endTime: "17:00" }],
          type: "blocked",
          reason: "",
        });
      } else {
        const error = await response.json();
        alert(error.message || "Failed to create block");
      }
    } catch (error) {
      alert("Failed to create block");
    }
  };

  // Handle block editing
  const handleEditBlock = (exception) => {
    setEditingException(exception);

    // Format dates properly without timezone offset
    const startDate = new Date(exception.startDate);
    const endDate = new Date(exception.endDate);

    // Use local date string to avoid timezone issues
    const formatDateLocal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setBlockForm({
      startDate: formatDateLocal(startDate),
      endDate: formatDateLocal(endDate),
      isFullDayBlock:
        !exception.timeBlocks || exception.timeBlocks.length === 0,
      timeBlocks:
        exception.timeBlocks && exception.timeBlocks.length > 0
          ? exception.timeBlocks
          : [{ startTime: "09:00", endTime: "17:00" }],
      type: exception.type || "blocked",
      reason: exception.reason || "",
    });
    setShowBlockModal(true);
  };

  // Handle block removal
  const handleRemoveBlock = async (exceptionId) => {
    if (!window.confirm("Are you sure you want to remove this block?")) return;

    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${API_BASE_URL}/api/schedule-exceptions/${exceptionId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        alert("Block removed successfully!");
        fetchExceptions();
      } else {
        const error = await response.json();
        alert(error.message || "Failed to remove block");
      }
    } catch (error) {
      alert("Failed to remove block");
    }
  };

  // Time block management
  const addTimeBlock = () => {
    setBlockForm((prev) => ({
      ...prev,
      timeBlocks: [
        ...prev.timeBlocks,
        { startTime: "09:00", endTime: "17:00" },
      ],
    }));
  };

  const removeTimeBlock = (index) => {
    setBlockForm((prev) => ({
      ...prev,
      timeBlocks: prev.timeBlocks.filter((_, i) => i !== index),
    }));
  };

  const updateTimeBlock = (index, field, value) => {
    setBlockForm((prev) => ({
      ...prev,
      timeBlocks: prev.timeBlocks.map((block, i) =>
        i === index ? { ...block, [field]: value } : block,
      ),
    }));
  };

  // Date navigation
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format date
  const formatMonthYear = () => {
    return currentDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  // Check if date is blocked
  const isDateBlocked = (date) => {
    return exceptions.some((ex) => {
      const exDate = new Date(date);
      exDate.setHours(0, 0, 0, 0);
      const exStart = new Date(ex.startDate);
      exStart.setHours(0, 0, 0, 0);
      const exEnd = new Date(ex.endDate);
      exEnd.setHours(0, 0, 0, 0);

      return (
        ["active", "pending_approval"].includes(ex.status) &&
        exDate >= exStart &&
        exDate <= exEnd
      );
    });
  };

  // Get bookings for date (only active bookings - not cancelled)
  const getBookingsForDate = (date) => {
    return bookings.filter((booking) => {
      // Use T12:00:00 to parse as local noon, avoiding UTC midnight off-by-one shift
      const bookingDate = new Date((booking.appointmentDate || booking.date) + "T12:00:00");
      return bookingDate.toDateString() === date.toDateString();
    });
  };

  // Get active (non-cancelled) bookings for date
  const getActiveBookingsForDate = (date) => {
    return bookings.filter((booking) => {
      const bookingDate = new Date((booking.appointmentDate || booking.date) + "T12:00:00");
      return (
        bookingDate.toDateString() === date.toDateString() &&
        booking.status !== "cancelled"
      );
    });
  };

  // Render weekly schedule
  const renderWeeklySchedule = () => {
    if (!schedule || !schedule.weeklyHours) {
      return (
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <p>Loading schedule...</p>
        </div>
      );
    }

    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const isMobile = window.innerWidth < 768;

    return (
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: isMobile ? "12px" : "16px",
          marginBottom: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        {/* Apply Business Hours Card for R7 */}
        {isBoothRenter && salonHours && (
          <>
            <div
              style={{
                marginBottom: "16px",
                padding: "16px",
                background: "#eff6ff",
                borderRadius: "8px",
                border: "1px solid #bfdbfe",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "start",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <span style={{ fontSize: "24px" }}>⚡</span>
                <div style={{ flex: 1 }}>
                  <h3
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: isMobile ? "14px" : "16px",
                      color: "#1e40af",
                      fontWeight: "600",
                    }}
                  >
                    Apply Business Hours to My Schedule
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      fontSize: isMobile ? "12px" : "13px",
                      color: "#3b82f6",
                      lineHeight: "1.5",
                    }}
                  >
                    Set your schedule to match the business operating hours
                    shown below, then customize individual days as needed.
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  // Apply salon hours to schedule
                  const dayMapping = {
                    Monday: "monday",
                    Tuesday: "tuesday",
                    Wednesday: "wednesday",
                    Thursday: "thursday",
                    Friday: "friday",
                    Saturday: "saturday",
                    Sunday: "sunday",
                  };

                  const newWeeklyHours = {};
                  Object.entries(salonHours).forEach(([day, hours]) => {
                    const lowercaseDay = dayMapping[day];
                    if (lowercaseDay) {
                      newWeeklyHours[lowercaseDay] = {
                        isAvailable: hours.isOpen,
                        start: hours.openTime || "09:00",
                        end: hours.closeTime || "17:00",
                      };
                    }
                  });

                  setSchedule((prev) => ({
                    ...prev,
                    weeklyHours: newWeeklyHours,
                  }));
                }}
                style={{
                  padding: isMobile ? "8px 16px" : "10px 20px",
                  background: "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: isMobile ? "13px" : "14px",
                  fontWeight: "600",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                Apply Business Hours
              </button>
            </div>

            {/* Salon Hours Info Card */}
            <div
              style={{
                marginBottom: "16px",
                padding: "16px",
                background: "#fef2f2",
                borderRadius: "8px",
                border: "1px solid #fecaca",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "start", gap: "12px" }}
              >
                <span style={{ fontSize: "24px" }}>🔴</span>
                <div style={{ flex: 1 }}>
                  <h3
                    style={{
                      margin: "0 0 12px 0",
                      fontSize: isMobile ? "14px" : "16px",
                      color: "#991b1b",
                      fontWeight: "600",
                    }}
                  >
                    Salon Business Hours
                  </h3>
                  <p
                    style={{
                      margin: "0 0 12px 0",
                      fontSize: isMobile ? "12px" : "13px",
                      color: "#dc2626",
                    }}
                  >
                    Your hours must be within shop hours. Shop is open:
                  </p>
                  <ul
                    style={{
                      margin: 0,
                      padding: "0 0 0 20px",
                      listStyle: "disc",
                      fontSize: isMobile ? "12px" : "14px",
                      color: "#991b1b",
                    }}
                  >
                    {Object.entries(salonHours).map(([day, hours]) => {
                      const formatTime = (time) => {
                        if (!time) return "";
                        const [h, m] = time.split(":");
                        const hour = parseInt(h);
                        const ampm = hour >= 12 ? "PM" : "AM";
                        const hour12 =
                          hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                        return `${hour12}:${m} ${ampm}`;
                      };

                      return (
                        <li key={day} style={{ marginBottom: "4px" }}>
                          <strong>{day}:</strong>{" "}
                          {hours.isOpen
                            ? `${formatTime(hours.openTime)} - ${formatTime(hours.closeTime)}`
                            : "Closed"}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}

        <div
          style={{
            marginBottom: "16px",
            padding: isMobile ? "10px" : "12px",
            background: "#f0f9ff",
            borderRadius: "8px",
            border: "1px solid #bae6fd",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: isMobile ? "12px" : "13px",
              color: "#0369a1",
              lineHeight: "1.5",
            }}
          >
            <strong>📅 Weekly Schedule:</strong> Set your regular availability
            for each day of the week. Check the box to mark a day as available,
            then set your working hours. Click "Save" to update your schedule.
          </p>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <h2
            style={{
              fontSize: isMobile ? "16px" : "18px",
              fontWeight: "600",
              margin: 0,
            }}
          >
            Weekly Schedule
          </h2>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button
              onClick={() => {
                setSchedule((prev) => {
                  const updated = { ...prev, weeklyHours: {} };
                  Object.keys(prev.weeklyHours).forEach((day) => {
                    updated.weeklyHours[day] = {
                      ...prev.weeklyHours[day],
                      isAvailable: true,
                    };
                  });
                  return updated;
                });
              }}
              style={{
                padding: isMobile ? "4px 8px" : "6px 12px",
                background: "#10b981",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: isMobile ? "11px" : "12px",
                fontWeight: "500",
              }}
            >
              Enable All
            </button>
            <button
              onClick={() => {
                setSchedule((prev) => {
                  const updated = { ...prev, weeklyHours: {} };
                  Object.keys(prev.weeklyHours).forEach((day) => {
                    updated.weeklyHours[day] = {
                      ...prev.weeklyHours[day],
                      isAvailable: false,
                    };
                  });
                  return updated;
                });
              }}
              style={{
                padding: isMobile ? "4px 8px" : "6px 12px",
                background: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: isMobile ? "11px" : "12px",
                fontWeight: "500",
              }}
            >
              Disable All
            </button>
            <button
              onClick={() => handleScheduleUpdate(schedule.weeklyHours)}
              style={{
                padding: isMobile ? "6px 12px" : "8px 16px",
                background: "#2563eb",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: "500",
              }}
            >
              Save
            </button>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: isMobile ? "8px" : "12px",
          }}
        >
          {days.map((day) => {
            const daySchedule = schedule.weeklyHours[day] || {
              isAvailable: false,
              start: "09:00",
              end: "17:00",
            };
            return (
              <div
                key={day}
                style={{
                  padding: isMobile ? "10px" : "12px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  background: daySchedule.isAvailable ? "white" : "#f9fafb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: isMobile ? "13px" : "14px",
                      textTransform: "capitalize",
                    }}
                  >
                    {day}
                  </span>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={daySchedule.isAvailable}
                      onChange={(e) => {
                        setSchedule((prev) => ({
                          ...prev,
                          weeklyHours: {
                            ...prev.weeklyHours,
                            [day]: {
                              ...(prev.weeklyHours[day] || {
                                start: "09:00",
                                end: "17:00",
                              }),
                              isAvailable: e.target.checked,
                            },
                          },
                        }));
                      }}
                      style={{ width: "14px", height: "14px" }}
                    />
                    <span
                      style={{
                        fontSize: isMobile ? "11px" : "12px",
                        color: "#6b7280",
                      }}
                    >
                      Available
                    </span>
                  </label>
                </div>
                {daySchedule.isAvailable && (
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="time"
                      value={daySchedule.start || "09:00"}
                      onChange={(e) => {
                        setSchedule((prev) => ({
                          ...prev,
                          weeklyHours: {
                            ...prev.weeklyHours,
                            [day]: {
                              ...(prev.weeklyHours[day] || {
                                isAvailable: true,
                                start: "09:00",
                                end: "17:00",
                              }),
                              start: e.target.value,
                            },
                          },
                        }));
                      }}
                      style={{
                        flex: 1,
                        padding: isMobile ? "4px" : "6px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        fontSize: isMobile ? "11px" : "12px",
                      }}
                    />
                    <span
                      style={{
                        color: "#6b7280",
                        fontSize: isMobile ? "10px" : "12px",
                      }}
                    >
                      to
                    </span>
                    <input
                      type="time"
                      value={daySchedule.end || "17:00"}
                      onChange={(e) => {
                        setSchedule((prev) => ({
                          ...prev,
                          weeklyHours: {
                            ...prev.weeklyHours,
                            [day]: {
                              ...(prev.weeklyHours[day] || {
                                isAvailable: true,
                                start: "09:00",
                                end: "17:00",
                              }),
                              end: e.target.value,
                            },
                          },
                        }));
                      }}
                      style={{
                        flex: 1,
                        padding: isMobile ? "4px" : "6px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        fontSize: isMobile ? "11px" : "12px",
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render calendar
  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    const isMobile = window.innerWidth < 768;

    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          style={{ padding: isMobile ? "6px" : "8px" }}
        ></div>,
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday = new Date().toDateString() === date.toDateString();
      const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
      const isBlocked = isDateBlocked(date);
      const dayBookings = getBookingsForDate(date);
      const activeBookings = getActiveBookingsForDate(date);
      const hasBookings = dayBookings.length > 0;
      const hasActiveBookings = activeBookings.length > 0;

      days.push(
        <div
          key={day}
          onClick={() => {
            if (hasBookings) {
              // Show bookings for this date
              if (dayBookings.length === 1) {
                navigate(
                  `/dashboard/freelancer/bookings/${dayBookings[0]._id}`,
                );
              } else {
                // Show modal with all bookings for this date
                alert(
                  `${dayBookings.length} bookings on this date. Full modal coming soon.`,
                );
              }
            }
          }}
          style={{
            padding: isMobile ? "6px" : "8px",
            minHeight: isMobile ? "50px" : "80px",
            border: "1px solid #e5e7eb",
            borderRadius: "6px",
            background: isPast
              ? "#f3f4f6"
              : isToday
                ? "#dbeafe"
                : isBlocked
                  ? "#fee2e2"
                  : hasBookings
                    ? "#d1fae5"
                    : "white",
            cursor: hasBookings ? "pointer" : "default",
            transition: "all 0.2s",
            position: "relative",
            opacity: isPast ? 0.5 : 1,
          }}
          onMouseEnter={(e) => {
            if (hasBookings || (!isPast && !isBlocked)) {
              e.currentTarget.style.transform = "scale(1.02)";
              e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                fontWeight: isToday ? "700" : "500",
                fontSize: isMobile ? "12px" : "14px",
                marginBottom: "4px",
                color: isPast ? "#9ca3af" : "inherit",
              }}
            >
              {day}
            </div>
            {!isBlocked && !isPast && !hasActiveBookings && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const selectedDate = new Date(year, month, day);
                  const year2 = selectedDate.getFullYear();
                  const month2 = String(selectedDate.getMonth() + 1).padStart(
                    2,
                    "0",
                  );
                  const day2 = String(selectedDate.getDate()).padStart(2, "0");
                  const dateStr = `${year2}-${month2}-${day2}`;
                  setEditingException(null);
                  setBlockForm({
                    startDate: dateStr,
                    endDate: dateStr,
                    isFullDayBlock: true,
                    timeBlocks: [{ startTime: "09:00", endTime: "17:00" }],
                    type: "blocked",
                    reason: "",
                  });
                  setShowBlockModal(true);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "14px",
                  padding: "2px",
                  lineHeight: 1,
                  opacity: 0.6,
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
                title="Block this date"
              >
                🚫
              </button>
            )}
            {hasActiveBookings && !isPast && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasActiveBookings) {
                    alert(
                      "Cannot block a date with active bookings. Please cancel bookings first.",
                    );
                  }
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "not-allowed",
                  fontSize: "14px",
                  padding: "2px",
                  lineHeight: 1,
                  opacity: 0.3,
                }}
                title="Cannot block - has active bookings"
              >
                🚫
              </button>
            )}
          </div>
          {hasBookings && (
            <div
              style={{
                fontSize: isMobile ? "9px" : "10px",
                color: "#065f46",
                marginTop: "2px",
              }}
            >
              {dayBookings.slice(0, 2).map((b, idx) => (
                <div
                  key={idx}
                  style={{
                    marginBottom: "1px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {b.startTime} - {b.serviceName || "Service"}
                </div>
              ))}
              {dayBookings.length > 2 && (
                <div style={{ fontSize: "9px", color: "#047857" }}>
                  +{dayBookings.length - 2} more
                </div>
              )}
            </div>
          )}
          {isBlocked && (
            <div
              style={{ fontSize: isMobile ? "16px" : "18px", marginTop: "2px" }}
            >
              🚫
            </div>
          )}
        </div>,
      );
    }

    return (
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: isMobile ? "12px" : "16px",
          marginBottom: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            marginBottom: "16px",
            padding: isMobile ? "10px" : "12px",
            background: "#fef3c7",
            borderRadius: "8px",
            border: "1px solid #fcd34d",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: isMobile ? "12px" : "13px",
              color: "#92400e",
              lineHeight: "1.5",
            }}
          >
            <strong>🗓️ Calendar View:</strong> See your monthly availability at
            a glance. Dates with bookings show a green indicator. Click "🚫
            Block" below to block specific dates or time ranges when you're
            unavailable.
          </p>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <button
            onClick={goToPrevious}
            style={{
              padding: "6px 12px",
              background: "#f3f4f6",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            ‹
          </button>
          <h2
            style={{
              fontSize: isMobile ? "16px" : "18px",
              fontWeight: "600",
              margin: 0,
            }}
          >
            {formatMonthYear()}
          </h2>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={goToToday}
              style={{
                padding: "6px 12px",
                background: "#f3f4f6",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: isMobile ? "11px" : "13px",
              }}
            >
              Today
            </button>
            <button
              onClick={goToNext}
              style={{
                padding: "6px 12px",
                background: "#f3f4f6",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              ›
            </button>
          </div>
        </div>

        <div style={{ width: "100%", overflowX: "auto" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: isMobile ? "4px" : "8px",
              minWidth: isMobile ? "280px" : "auto",
            }}
          >
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div
                key={day}
                style={{
                  fontWeight: "600",
                  fontSize: isMobile ? "11px" : "12px",
                  color: "#6b7280",
                  textAlign: "center",
                  padding: "8px 0",
                }}
              >
                {day}
              </div>
            ))}
            {days}
          </div>
        </div>
      </div>
    );
  };

  // Render blocked dates list
  const renderBlockedDates = () => {
    if (exceptions.length === 0) return null;

    const isMobile = window.innerWidth < 768;

    return (
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: isMobile ? "12px" : "16px",
          marginBottom: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            fontSize: isMobile ? "16px" : "18px",
            fontWeight: "600",
            marginBottom: "12px",
          }}
        >
          Blocked Dates
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {exceptions
            .filter((ex) => ["active", "pending_approval"].includes(ex.status))
            .map((ex) => (
              <div
                key={ex._id}
                style={{
                  padding: isMobile ? "10px" : "12px",
                  background:
                    ex.status === "pending_approval" ? "#fff7ed" : "#fee2e2",
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: isMobile ? "13px" : "14px",
                      fontWeight: "600",
                    }}
                  >
                    {new Date(ex.startDate).toLocaleDateString()} -{" "}
                    {new Date(ex.endDate).toLocaleDateString()}
                  </div>
                  {ex.status === "pending_approval" && (
                    <div
                      style={{
                        fontSize: isMobile ? "11px" : "12px",
                        color: "#9a3412",
                        marginTop: "4px",
                        fontWeight: "500",
                      }}
                    >
                      Pending approval
                    </div>
                  )}
                  {ex.reason && (
                    <div
                      style={{
                        fontSize: isMobile ? "11px" : "12px",
                        color: "#6b7280",
                        marginTop: "4px",
                      }}
                    >
                      {ex.reason}
                    </div>
                  )}
                  {ex.timeBlocks && ex.timeBlocks.length > 0 && (
                    <div
                      style={{
                        fontSize: isMobile ? "11px" : "12px",
                        color: "#2d6da3",
                        marginTop: "4px",
                      }}
                    >
                      Time blocks:{" "}
                      {ex.timeBlocks
                        .map((tb) => `${tb.startTime}-${tb.endTime}`)
                        .join(", ")}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button
                    onClick={() => handleEditBlock(ex)}
                    style={{
                      padding: isMobile ? "4px 8px" : "6px 12px",
                      background: "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: isMobile ? "11px" : "12px",
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleRemoveBlock(ex._id)}
                    style={{
                      padding: isMobile ? "4px 8px" : "6px 12px",
                      background: "#dc2626",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: isMobile ? "11px" : "12px",
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  };

  return (
    <FreelancerSidebarLayout>
      <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
        <h1
          style={{ fontSize: "24px", fontWeight: "700", marginBottom: "20px" }}
        >
          Schedule & Calendar
        </h1>

        {renderWeeklySchedule()}
        {renderCalendar()}
        {renderBlockedDates()}
      </div>

      {/* Block Modal */}
      {showBlockModal && (
        <>
          <div
            onClick={() => setShowBlockModal(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "90%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflowY: "auto",
              zIndex: 1001,
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "600" }}>
                {editingException ? "✏️ Edit Block" : "🚫 Block Date/Time"}
              </h2>
              <button
                onClick={() => setShowBlockModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                  padding: "4px 8px",
                  lineHeight: "1",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Start Date
              </label>
              <input
                type="date"
                value={blockForm.startDate}
                onChange={(e) =>
                  setBlockForm((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                End Date
              </label>
              <input
                type="date"
                value={blockForm.endDate}
                onChange={(e) =>
                  setBlockForm((prev) => ({ ...prev, endDate: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={blockForm.isFullDayBlock}
                  onChange={(e) =>
                    setBlockForm((prev) => ({
                      ...prev,
                      isFullDayBlock: e.target.checked,
                    }))
                  }
                  style={{ width: "18px", height: "18px" }}
                />
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  Block full day(s)
                </span>
              </label>
            </div>

            {!blockForm.isFullDayBlock && (
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Time Blocks
                </label>
                {blockForm.timeBlocks.map((block, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginBottom: "8px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="time"
                      value={block.startTime}
                      onChange={(e) =>
                        updateTimeBlock(index, "startTime", e.target.value)
                      }
                      style={{
                        flex: 1,
                        padding: "6px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                      }}
                    />
                    <span style={{ color: "#6b7280" }}>to</span>
                    <input
                      type="time"
                      value={block.endTime}
                      onChange={(e) =>
                        updateTimeBlock(index, "endTime", e.target.value)
                      }
                      style={{
                        flex: 1,
                        padding: "6px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                      }}
                    />
                    {blockForm.timeBlocks.length > 1 && (
                      <button
                        onClick={() => removeTimeBlock(index)}
                        style={{
                          padding: "6px 10px",
                          background: "#dc2626",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addTimeBlock}
                  style={{
                    padding: "6px 12px",
                    background: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                >
                  + Add Time Block
                </button>
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Reason (optional)
              </label>
              <input
                type="text"
                value={blockForm.reason}
                onChange={(e) =>
                  setBlockForm((prev) => ({ ...prev, reason: e.target.value }))
                }
                placeholder="e.g., Vacation, Personal day"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowBlockModal(false)}
                style={{
                  padding: "8px 16px",
                  background: "white",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBlockSubmit}
                style={{
                  padding: "8px 16px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {editingException ? "Update Block" : "Block Dates"}
              </button>
            </div>
          </div>
        </>
      )}
    </FreelancerSidebarLayout>
  );
};

export default FreelancerSchedule;
